import { leafDescendantIds } from "./clusterTree.js";
import { promptCount } from "./promptMetrics.js";

// Far above a column pair's leaves x visible entries (staging: 10,727 rows for
// 64 topics x 64 tools). A full page means rows may be missing.
export const LEAF_ROLLUP_ROW_LIMIT = 50000;

// Leaf id -> the visible node whose subtree holds it. Null when two visible
// nodes share a leaf (a node listed beside its own ancestor), where one leaf's
// count would belong to both.
export function leafToVisibleNodes(tree, visibleIds) {
    const map = new Map();
    for (const id of visibleIds ?? []) {
        for (const leaf of leafDescendantIds(tree, id)) {
            const owner = map.get(String(leaf));
            if (owner != null && owner !== String(id)) return null;
            map.set(String(leaf), String(id));
        }
    }
    return map;
}

/**
 * Distinct-prompt counts per visible topic node (and per `groupDims` key) from
 * one read grouped by leaf, instead of one read per node.
 *
 * Summing leaves double-counts a prompt observed in two leaves, so a second
 * read over the same filter, without the leaf dimension, gives the distinct
 * total for each group key and metric. Each total equals the sum of its leaf
 * counts exactly when no such prompt exists, and only then are the sums
 * returned. Returns null on any mismatch, on a full page, or when a counted
 * leaf maps to no visible node, so the caller counts each node directly.
 *
 * `filter` must restrict rows to the visible nodes' subtrees.
 * `metrics` is [{ alias, predicate? }]; each becomes a distinct logical-prompt
 * count, filtered by its predicate when given.
 * Resolves to [{ node, ...groupDimValues, ...metricValues }].
 */
export async function rollUpLeafCounts({
    root,
    filter,
    timeRange,
    leafField,
    leafToVisible,
    groupDims = [],
    metrics,
    runQuery,
    rowsToObjects,
    limit = LEAF_ROLLUP_ROW_LIMIT,
}) {
    if (!leafToVisible) return null;
    const metricSpecs = metrics.map(({ alias, predicate }) => promptCount(alias, "p", predicate));
    const leafSpec = {
        root, timeRange, limit,
        output: { dimensions: [{ field: leafField, outputAlias: "leaf" }, ...groupDims], metrics: metricSpecs },
    };
    const totalSpec = { root, timeRange, limit, output: { dimensions: groupDims, metrics: metricSpecs } };
    if (filter) {
        leafSpec.filter = filter;
        totalSpec.filter = filter;
    }
    const [leafRows, totalRows] = (await Promise.all([runQuery(leafSpec), runQuery(totalSpec)])).map(rowsToObjects);
    if (leafRows.length >= limit || totalRows.length >= limit) return null;

    const groupKey = (row) => JSON.stringify(groupDims.map((dim) => String(row[dim.outputAlias] ?? "")));
    const leafSums = new Map();
    const byNode = new Map();
    for (const row of leafRows) {
        const key = groupKey(row);
        const node = leafToVisible.get(String(row.leaf));
        for (const { alias } of metrics) {
            const value = Number(row[alias]) || 0;
            if (!value) continue;
            if (node == null) return null;
            const sumKey = `${key}|${alias}`;
            leafSums.set(sumKey, (leafSums.get(sumKey) || 0) + value);
            const nodeKey = `${node}|${key}`;
            let out = byNode.get(nodeKey);
            if (!out) {
                out = { node, ...Object.fromEntries(groupDims.map((dim) => [dim.outputAlias, row[dim.outputAlias]])) };
                for (const metric of metrics) out[metric.alias] = 0;
                byNode.set(nodeKey, out);
            }
            out[alias] += value;
        }
    }
    const totals = new Map();
    for (const row of totalRows) {
        const key = groupKey(row);
        for (const { alias } of metrics) {
            const value = Number(row[alias]) || 0;
            if (value) totals.set(`${key}|${alias}`, value);
        }
    }
    if (totals.size !== leafSums.size) return null;
    for (const [sumKey, sum] of leafSums) {
        if (totals.get(sumKey) !== sum) return null;
    }
    return [...byNode.values()];
}
